import { ReactNode } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import ChipV2, { ChipV2Tone } from '../atoms/ChipV2';
import { SingleLineText } from '../atoms/SingleLineText';

export type ObjectiveKpiAccent = 'pink' | 'blue' | 'green';

export type ObjectiveKpiFooterTag = {
  label: string;
  tone: ChipV2Tone;
};

export type ObjectiveKpiCardProps = {
  accent: ObjectiveKpiAccent;
  icon: ReactNode;
  title: string;
  valuePrimary: string;
  valueSecondary?: string;
  progressPercent: number;
  footer: string;
  footerTag?: ObjectiveKpiFooterTag;
  footerAccent?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
};

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

const FooterTagDot = styled('span')(() => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: 'currentColor',
  flexShrink: 0,
}));

const FooterTagLabel = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
}));

export const ObjectiveKpiCard = ({
  accent,
  icon,
  title,
  valuePrimary,
  valueSecondary,
  progressPercent,
  footer,
  footerTag,
  footerAccent = false,
  onClick,
  'data-testid': testId,
}: ObjectiveKpiCardProps) => {
  const theme = useTheme();
  const accentColor =
    accent === 'green' ? theme.palette.green[60] : accent === 'blue' ? theme.palette.blue[60] : theme.palette.red[60];
  const footerColor = footerAccent ? theme.palette.red[60] : theme.palette.neutral[60];

  return (
    <CardContainer data-testid={testId} onClick={onClick} $clickable={!!onClick}>
      <AccentBar $color={accentColor} />
      <CardBody>
        <HeaderRow>
          {icon}
          <CardTitle text={title} />
        </HeaderRow>
        <MetricRow>
          <MetricPrimary>{valuePrimary}</MetricPrimary>
          {valueSecondary ? <MetricSecondary>{valueSecondary}</MetricSecondary> : null}
        </MetricRow>
        <ProgressTrack>
          <ProgressFill $color={accentColor} $percent={clampPercent(progressPercent)} />
        </ProgressTrack>
        <FooterRow>
          <FooterText text={footer} sx={{ color: footerColor, textAlign: 'left' }} />
          {footerTag ? (
            <ChipV2
              label={
                <FooterTagLabel>
                  <FooterTagDot />
                  {footerTag.label}
                </FooterTagLabel>
              }
              tone={footerTag.tone}
              size="small"
              numeric
              sx={{ marginLeft: 'auto', flexShrink: 0 }}
            />
          ) : null}
        </FooterRow>
      </CardBody>
    </CardContainer>
  );
};

export default ObjectiveKpiCard;

const CardContainer = styled('div', { shouldForwardProp: (prop) => prop !== '$clickable' })<{ $clickable: boolean }>(
  ({ theme, $clickable }) => ({
    flex: '1 1 220px',
    minWidth: 220,
    backgroundColor: theme.palette.neutral.white,
    borderRadius: 12,
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    cursor: $clickable ? 'pointer' : 'default',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    ...($clickable
      ? {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
          },
        }
      : {}),
  }),
);

const AccentBar = styled('div')<{ $color: string }>(({ $color }) => ({
  height: 4,
  width: '100%',
  backgroundColor: $color,
}));

const CardBody = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 16px 16px',
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}));

const CardTitle = styled(SingleLineText)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[60],
  lineHeight: 1.2,
}));

const MetricRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 2,
  minHeight: 36,
}));

const MetricPrimary = styled('span')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '28px',
  fontWeight: 700,
  lineHeight: 1.1,
  color: '#111827',
}));

const MetricSecondary = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.neutral[50],
}));

const ProgressTrack = styled('div')(({ theme }) => ({
  width: '100%',
  height: 4,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[15],
  overflow: 'hidden',
}));

const ProgressFill = styled('div')<{ $color: string; $percent: number }>(({ $color, $percent }) => ({
  height: '100%',
  width: `${$percent}%`,
  borderRadius: 999,
  backgroundColor: $color,
  transition: 'width 0.2s ease',
}));

const FooterRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: 8,
  minWidth: 0,
  width: '100%',
}));

const FooterText = styled(SingleLineText)(() => ({
  fontSize: '13px',
  lineHeight: 1.3,
  flex: '1 1 auto',
  minWidth: 0,
  textAlign: 'left',
}));
